import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { brand, logo, waLink } from "@/content/site";
import { Ltr, WhatsAppIcon, Arrow } from "@/components/apex/primitives";

const Footer = () => {
  const { t, isRtl, toggle } = useLang();
  const links = [
    { href: "/designs", label: t.nav.designs },
    { href: "/#visualizer", label: t.nav.visualizer },
    { href: "/#features", label: t.nav.features },
    { href: "/#about", label: t.nav.about },
    { href: "/#faq", label: t.nav.faq },
    { href: "/#contact", label: t.nav.contact },
  ];

  return (
    <footer data-testid="site-footer" className="ap-dark pb-[max(2rem,env(safe-area-inset-bottom))] pt-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col gap-8 border-b border-white/10 pb-14 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="ap-display max-w-xl text-[1.9rem] text-white sm:text-[2.4rem]">{t.footer.tagline}</h2>
          <a
            href={waLink(t.float.defaultMsg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-cta"
            className="ap-btn shrink-0"
          >
            {t.nav.cta}
            <Arrow />
          </a>
        </div>

        <div className="grid gap-12 py-14 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <img src={logo.src} alt={isRtl ? logo.alt_ar : logo.alt_en} loading="lazy" className="h-8 w-auto brightness-0 invert" />
            <p className="mt-6 max-w-sm text-[14px] leading-[1.9] text-white/60">{t.manifesto.body}</p>
            <div className="mt-7 space-y-3">
              <a href={brand.telUrl} data-testid="footer-phone" className="group flex items-center gap-3 text-white hover:text-[var(--yellow)]">
                <span className="ap-icon h-10 w-10">
                  <Phone className="h-4 w-4" strokeWidth={2} />
                </span>
                <Ltr className="ap-mono text-[15px] font-semibold">{brand.phoneDisplay}</Ltr>
              </a>
              <a
                href={waLink(t.float.defaultMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="group flex items-center gap-3 text-white hover:text-[var(--wa)]"
              >
                <span className="ap-icon h-10 w-10 !bg-[var(--wa)] !text-white">
                  <WhatsAppIcon className="h-4 w-4" />
                </span>
                <span className="text-[15px] font-semibold">WhatsApp</span>
              </a>
            </div>
          </div>

          <nav aria-label={t.footer.nav}>
            <h3 className="ap-display text-lg text-white">{t.footer.nav}</h3>
            <ul className="mt-6 space-y-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.href.replace("/", "").replace("#", "")}`}
                    className="text-[14.5px] text-white/65 transition-colors duration-300 hover:text-[var(--yellow)]"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="ap-display text-lg text-white">{t.footer.contact}</h3>
            <ul className="mt-6 space-y-3 text-[14.5px] text-white/65">
              <li>
                <Ltr>{brand.phoneDisplay}</Ltr>
              </li>
              <li>WhatsApp — APEX DOORS</li>
              <li>
                <button
                  type="button"
                  onClick={toggle}
                  data-testid="footer-language-switch"
                  aria-label={t.langSwitchAria}
                  className="ap-mono mt-2 rounded-full bg-white/10 px-4 py-2 text-[12px] font-bold tracking-[0.15em] text-white transition-colors hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]"
                >
                  {t.langLabel}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 pb-16 pt-7 sm:flex-row sm:items-center sm:justify-between sm:pb-0 sm:pe-24">
          <p className="text-[13px] text-white/45">
            © {new Date().getFullYear()} APEX DOORS. {t.footer.rights}.
          </p>
          <Link
            to="/privacy"
            data-testid="footer-privacy"
            className="text-[13px] text-white/55 underline-offset-4 transition-colors hover:text-[var(--yellow)] hover:underline"
          >
            {t.footer.privacy}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
